import Tooltip from '@mui/material/Tooltip';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { pdfApi, wordApi } from '../../../api';
import downloadSvg from '../../../assets/img/download.svg';
import folderSvg from '../../../assets/img/folder.svg';
import trashSvg from '../../../assets/img/trash.svg';
import { useModal } from '../../../shared/hooks';
import { TaskKeysType } from '../../../shared/types';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import { convertBytes } from '../../../utils/bytesConvertor';
import getFileSize from '../../../utils/getFileSize';

interface Props {
  id: string;
  url: string;
  type: 'pdf' | 'doc';
  remoteTaskId?: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  setFile: (file: { file: File; url: string } | null) => void;
  currentICLId: string;
  downloadFile: (url: string) => void;
}

export default function DocumentItem({
  id,
  url,
  type,
  remoteTaskId,
  handleChangeTaskByKey,
  setFile,
  currentICLId,
  downloadFile,
}: Props) {
  const { t } = useTranslation();
  const { isOpen: isModalOpen, close: setIsModalClose, open: setIsModalOpen } = useModal();
  const [fileSize, setFileSize] = React.useState<string | null>(null);

  useEffect(() => {
    getFileSize(url).then(size => setFileSize(size));
  }, [url]);

  const removeFile = async () => {
    try {
      if (remoteTaskId) {
        switch (type) {
          case 'pdf':
            await pdfApi.removePdf(url, currentICLId, id);
            handleChangeTaskByKey(id, 'pdfUrl', '');
            break;
          case 'doc':
            await wordApi.removeWord(url, currentICLId, id);
            handleChangeTaskByKey(id, 'wordUrl', '');
            break;
          default:
            break;
        }

        setFile(null);

        toast.success(t('Successfully removed!'));
      }
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }
  };

  const renderSize = () => {
    return (
      fileSize && (
        <div className="flex flex-col">
          <div className="text-[12px] lg:text-[16px]">
            {type === 'pdf' ? `${t('document')} PDF` : `${t('document')} Word`}
          </div>
          <div className="text-[12px] text-[#656D7B] lg:text-[16px]">
            {convertBytes(+fileSize).megabytes < 0
              ? `${Math.floor(convertBytes(+fileSize).megabytes)} ${t('mb')}`
              : `${Math.floor(convertBytes(+fileSize).kilobytes)} ${t('kb')}`}
          </div>
        </div>
      )
    );
  };

  return (
    <div className="flex items-center justify-between rounded-[10px] border-[1px] border-[#9CDFFC] bg-white p-[20px]">
      <div className="flex items-center">
        <div className="mr-[25px] flex items-center justify-center rounded-[10px] bg-[#DCF0FF] p-[15px]">
          <img src={folderSvg} alt="folder" />
        </div>

        {renderSize()}
      </div>

      <div className="flex items-center gap-[15px]">
        <Tooltip
          title={t('download file')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600"
            onClick={() => downloadFile(url)}
          >
            <img src={downloadSvg} alt="downloadSvg" />
          </button>
        </Tooltip>

        <Tooltip
          title={t('deleteFile')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600"
            onClick={() => setIsModalOpen()}
          >
            <img src={trashSvg} alt="mic" />
          </button>
        </Tooltip>
      </div>

      <ItemActionModal
        isOpen={isModalOpen}
        close={setIsModalClose}
        title={'deleteFile'}
        onHandle={() => removeFile()}
        buttonTitle={'delete'}
        isSubmitButtonDisabled={false}
      />
    </div>
  );
}
